import * as React from "react"

import { DataTable, Pagination, type Column, type SortState } from "@/components/ui/data-table"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { RULE_CATALOG } from "@/features/review/data/rule-catalog"
import type { FilterDef, TableSort } from "@/lib/table"
import { useTable } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"

type Severity = "error" | "warning" | "info"

interface Row {
  id: string
  label: string
  section: string | null
  severity: Severity
  group: string
  authorities: string
}

const SECTION_LABEL: Record<string, string> = {
  front_panel: "Front panel",
  ingredients: "Ingredients",
  first_aid: "First aid",
  precautionary_statements: "Precautionary",
  directions_for_use: "Directions",
  storage_disposal: "Storage & disposal",
}

const GROUP_LABEL: Record<string, string> = {
  "g-format": "Format",
  "g-content": "Content",
  "g-graphics": "Graphics",
  "g-defensibility": "Defensibility",
  "g-consistency": "Consistency",
}

const ROWS: Row[] = RULE_CATALOG.map((r) => ({
  id: r.id,
  label: r.label,
  section: r.section ?? null,
  severity: (r.severity as Severity) ?? "info",
  group: r.groupId ?? "g-format",
  authorities: (r.authorities ?? []).map((a) => a.label).join(" · "),
}))

const SEVERITY_CLASSES: Record<Severity, string> = {
  error: "bg-people-tint text-people ring-people/20",
  warning: "bg-info-tint text-info ring-info/20",
  info: "bg-surface-sunken text-text-2 ring-border/50",
}

const SEVERITY_LABEL: Record<Severity, string> = {
  error: "Error",
  warning: "Warning",
  info: "Info",
}

function SeverityPill({ severity }: { severity: Severity }): JSX.Element {
  return (
    <span
      className={`inline-flex min-w-[72px] items-center justify-center gap-1 rounded-full px-2 py-px text-[10px] font-medium leading-4 ring-1 ring-inset ${SEVERITY_CLASSES[severity]}`}
    >
      <span className="size-1 rounded-full bg-current opacity-70" aria-hidden="true" />
      {SEVERITY_LABEL[severity]}
    </span>
  )
}

const SECTION_OPTIONS = Object.entries(SECTION_LABEL).map(([value, label]) => ({ value, label }))
const GROUP_OPTIONS = Object.entries(GROUP_LABEL).map(([value, label]) => ({ value, label }))

const FILTER_SCHEMA: FilterDef[] = [
  {
    id: "severity",
    label: "Severity",
    kind: "select",
    options: [
      { value: "error", label: "Error" },
      { value: "warning", label: "Warning" },
      { value: "info", label: "Info" },
    ],
    placeholder: "Any severity",
  },
  {
    id: "section",
    label: "Section",
    kind: "select",
    multi: true,
    options: SECTION_OPTIONS,
    placeholder: "Any section",
  },
  {
    id: "group",
    label: "Group",
    kind: "select",
    multi: true,
    options: GROUP_OPTIONS,
    placeholder: "Any group",
  },
]

const SORT_OPTIONS: SortOption[] = [
  { id: "id", label: "Rule ID", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "label", label: "Description", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "severity", label: "Severity", ascLabel: "Info first", descLabel: "Error first" },
]

const SEVERITY_ORDER: Record<Severity, number> = { info: 0, warning: 1, error: 2 }

export function DataTablePreviewRoute(): JSX.Element {
  const searchRef = React.useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>
  useSlashShortcut(searchRef)

  const fetchData = React.useCallback(
    ({
      page,
      pageSize,
      sort,
      filters,
      search,
    }: {
      page: number
      pageSize: number
      sort: TableSort | null
      filters: Record<string, unknown>
      search: string
    }) => {
      const needle = search.trim().toLowerCase()
      const severityFilter =
        typeof filters["severity"] === "string" ? (filters["severity"] as Severity) : null
      const sectionFilter = Array.isArray(filters["section"])
        ? (filters["section"] as string[])
        : []
      const groupFilter = Array.isArray(filters["group"]) ? (filters["group"] as string[]) : []

      const filtered = ROWS.filter((r) => {
        if (severityFilter && r.severity !== severityFilter) return false
        if (sectionFilter.length > 0 && (r.section == null || !sectionFilter.includes(r.section))) {
          return false
        }
        if (groupFilter.length > 0 && !groupFilter.includes(r.group)) return false
        if (!needle) return true
        return (
          r.id.toLowerCase().includes(needle) ||
          r.label.toLowerCase().includes(needle) ||
          r.authorities.toLowerCase().includes(needle)
        )
      })

      const sorted = sort
        ? [...filtered].sort((a, b) => {
            const dir = sort.dir === "asc" ? 1 : -1
            if (sort.field === "label") return a.label.localeCompare(b.label) * dir
            if (sort.field === "severity")
              return (SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]) * dir
            return a.id.localeCompare(b.id) * dir
          })
        : filtered

      const start = (page - 1) * pageSize
      return Promise.resolve({ rows: sorted.slice(start, start + pageSize), total: sorted.length })
    },
    [],
  )

  const { state, actions } = useTable<Row>({
    fetchData,
    defaultSort: { field: "id", dir: "asc" },
  })

  const columns = React.useMemo<Array<Column<Row>>>(
    () => [
      {
        id: "id",
        header: "Rule",
        width: "120px",
        cell: (r) => <span className="font-id tabular-nums text-body-sm text-text">{r.id}</span>,
      },
      {
        id: "label",
        header: "Description",
        width: "minmax(0,1.8fr)",
        cell: (r) => (
          <div className="min-w-0">
            <div className="truncate text-body text-text">{r.label}</div>
            <div className="mt-0.5 truncate text-caption text-text-3">{r.authorities}</div>
          </div>
        ),
      },
      {
        id: "severity",
        header: "Severity",
        width: "112px",
        priority: 2,
        cell: (r) => <SeverityPill severity={r.severity} />,
      },
      {
        id: "section",
        header: "Section",
        width: "160px",
        priority: 3,
        cell: (r) => (
          <span className="text-body-sm text-text-2">
            {r.section ? SECTION_LABEL[r.section] : "—"}
          </span>
        ),
      },
      {
        id: "group",
        header: "Group",
        width: "128px",
        priority: 3,
        cell: (r) => (
          <span className="text-body-sm text-text-2">{GROUP_LABEL[r.group] ?? r.group}</span>
        ),
      },
    ],
    [],
  )

  const sortState: SortState = state.sort
    ? { columnId: state.sort.field, dir: state.sort.dir }
    : null

  const setSort = React.useCallback(
    (s: SortState) => actions.setSort(s ? { field: s.columnId, dir: s.dir } : null),
    [actions],
  )

  return (
    <div className="flex h-full flex-col gap-5 px-6 py-5">
      <header>
        <div className="font-id text-eyebrow uppercase tracking-[0.16em] text-text-3">
          Primitive
        </div>
        <h1 className="mt-1 text-subtitle text-text">DataTable — full showcase</h1>
        <p className="mt-1 text-body-sm text-text-3">
          Search, filters (single + multi-select), sort and pagination — all driven by the generic{" "}
          <code className="font-id">useTable</code> hook and typed filter schema.
        </p>
      </header>

      <TableToolbar
        search={state.search}
        onSearchChange={actions.setSearch}
        searchRef={searchRef}
        searchPlaceholder="Search rule id, description or authority"
        searchAriaLabel="Search rules"
        searchShortcut="/"
        filterSchema={FILTER_SCHEMA}
        filterValues={state.filters}
        onFiltersChange={actions.setFilters}
        onClearAllFilters={actions.clearAllFilters}
        sortOptions={SORT_OPTIONS}
        sort={state.sort}
        onSortChange={actions.setSort}
      />

      <ActiveFilterChips
        schema={FILTER_SCHEMA}
        values={state.filters}
        onRemove={actions.clearFilter}
        onClearAll={actions.clearAllFilters}
      />

      <div className="min-h-0 flex-1">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<Row>
              label="Rules"
              columns={columns}
              rows={state.rows}
              rowKey={(r) => r.id}
              sort={sortState}
              onSortChange={setSort}
              stickyHeader
              loading={state.isLoading}
              className="rounded-none border-0"
            />
          </div>
          <div className="border-t border-border">
            <Pagination
              page={state.page}
              pageSize={state.pageSize}
              total={state.total}
              onPageChange={actions.setPage}
              onPageSizeChange={actions.setPageSize}
              pageSizes={[10, 25, 50]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
